import { useState, useCallback, useEffect } from "react";
import { api } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, RefreshCw, CloudDownload, Loader2 } from "lucide-react";

export default function Import() {
  const [file, setFile] = useState(null);
  const [year, setYear] = useState(2025);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  
  // YNAB sync state
  const [ynabStatus, setYnabStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    fetchYnabStatus();
  }, []);

  const fetchYnabStatus = async () => {
    try {
      const res = await api.get("/ynab/status");
      setYnabStatus(res.data);
    } catch (error) {
      console.error("Failed to fetch YNAB status:", error);
    }
  };

  const handleYnabSync = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      
      const res = await api.post("/ynab/sync", { year });
      setSyncResult(res.data);
      toast.success(`Synced ${res.data.transactions_synced} transactions from YNAB!`);
      
      // Refresh status
      await fetchYnabStatus();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to sync with YNAB";
      toast.error(message);
      setSyncResult({ success: false, message });
    } finally {
      setSyncing(false);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type === "text/csv" || droppedFile.name.endsWith(".csv")) {
        setFile(droppedFile);
      } else {
        toast.error("Please upload a CSV file");
      }
    }
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);

      const res = await api.post(`/import/ynab-csv?year=${year}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setResult(res.data);
      toast.success("Import successful!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "Never";
    return new Date(isoString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: "America/New_York"
    }) + " ET";
  };

  return (
    <div className="space-y-6 animate-slide-in-up">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Outfit'] tracking-tight text-slate-900">
          Import Data
        </h1>
        <p className="text-slate-500 mt-1">
          Sync your spending data from YNAB or import via CSV
        </p>
      </div>

      {/* YNAB Sync Card - Primary */}
      <Card className="border-sky-200 bg-gradient-to-br from-sky-50 to-white">
        <CardHeader>
          <CardTitle className="font-['Outfit'] text-lg flex items-center gap-2 text-sky-900">
            <CloudDownload className="h-5 w-5 text-sky-600" strokeWidth={1.5} />
            Sync from YNAB
          </CardTitle>
          <CardDescription>
            Automatically sync your categories and transactions from YNAB
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-700">Status:</span>
                {ynabStatus?.configured ? (
                  <span className="text-sm text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4" />
                    Connected
                  </span>
                ) : (
                  <span className="text-sm text-amber-600 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    Not configured
                  </span>
                )}
              </div>
              {ynabStatus?.last_sync && (
                <p className="text-xs text-slate-500">
                  Last synced: {formatDate(ynabStatus.last_sync)}
                </p>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                onClick={handleYnabSync}
                disabled={!ynabStatus?.configured || syncing}
                className="bg-sky-600 hover:bg-sky-700"
                data-testid="ynab-sync-btn"
              >
                {syncing ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Syncing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <RefreshCw className="h-4 w-4" />
                    Sync Now
                  </span>
                )}
              </Button>
            </div>
          </div>
          
          {/* Sync Result */}
          {syncResult && syncResult.success && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-emerald-800">Sync Complete!</p>
                  <div className="grid grid-cols-3 gap-4 mt-2">
                    <div>
                      <p className="text-2xl font-bold font-mono text-emerald-600">
                        {syncResult.categories_synced}
                      </p>
                      <p className="text-xs text-emerald-700">New categories</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold font-mono text-emerald-600">
                        {syncResult.transactions_synced}
                      </p>
                      <p className="text-xs text-emerald-700">Transactions</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold font-mono text-emerald-600">
                        {syncResult.spending_entries_created}
                      </p>
                      <p className="text-xs text-emerald-700">Spending entries</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {syncResult && !syncResult.success && (
            <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-rose-800">Sync Failed</p>
                  <p className="text-sm text-rose-700 mt-1">{syncResult.message}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CSV Upload Card */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Outfit'] text-lg flex items-center gap-2">
              <Upload className="h-5 w-5 text-slate-400" strokeWidth={1.5} />
              Upload YNAB CSV
            </CardTitle>
            <CardDescription>
              Alternative: Export your spending report from YNAB and upload it here
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Year</label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-[120px]" data-testid="import-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div
              className={`upload-zone relative rounded-lg p-8 text-center ${
                dragActive ? "dragging" : ""
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                data-testid="file-input"
              />
              
              <FileSpreadsheet className="h-12 w-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
              
              {file ? (
                <div>
                  <p className="font-medium text-slate-900">{file.name}</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              ) : (
                <div>
                  <p className="font-medium text-slate-700">
                    Drop your CSV file here
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    or click to browse
                  </p>
                </div>
              )}
            </div>

            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="w-full bg-slate-900 hover:bg-slate-800"
              data-testid="upload-btn"
            >
              {uploading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                  Importing...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Import CSV
                </span>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* CSV Result Card */}
        {result && (
          <Card className="border-slate-200 border-emerald-200 bg-emerald-50/50">
            <CardHeader>
              <CardTitle className="font-['Outfit'] text-lg flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="h-5 w-5" strokeWidth={1.5} />
                CSV Import Successful
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 border border-emerald-100">
                  <p className="text-3xl font-bold font-mono text-emerald-600">
                    {result.spending_entries_imported}
                  </p>
                  <p className="text-sm text-slate-600 mt-1">
                    Spending entries imported
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-emerald-100">
                  <p className="text-3xl font-bold font-mono text-emerald-600">
                    {result.categories_created}
                  </p>
                  <p className="text-sm text-slate-600 mt-1">
                    New categories created
                  </p>
                </div>
              </div>
              
              <p className="text-sm text-slate-600">
                Your YNAB data has been imported. You can now set budget goals and view your spending progress.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Instructions Card */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-['Outfit'] text-lg">
              How to sync from YNAB
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <CloudDownload className="h-4 w-4 text-sky-600" />
                  API Sync (Recommended)
                </h4>
                <ol className="space-y-2 text-sm text-slate-600">
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-sky-100 text-sky-700 text-xs flex items-center justify-center font-medium">1</span>
                    <span>Click the <strong>"Sync Now"</strong> button above</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-sky-100 text-sky-700 text-xs flex items-center justify-center font-medium">2</span>
                    <span>Wait for the sync to complete (usually 10-30 seconds)</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-sky-100 text-sky-700 text-xs flex items-center justify-center font-medium">3</span>
                    <span>Categories and spending data are automatically updated</span>
                  </li>
                </ol>
              </div>
              
              <div>
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4 text-slate-600" />
                  CSV Import (Alternative)
                </h4>
                <ol className="space-y-2 text-sm text-slate-600">
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-100 text-slate-700 text-xs flex items-center justify-center font-medium">1</span>
                    <span>In YNAB, go to <strong>Reports → Spending</strong></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-100 text-slate-700 text-xs flex items-center justify-center font-medium">2</span>
                    <span>Set date range to full year (Jan 1 - Dec 31)</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-100 text-slate-700 text-xs flex items-center justify-center font-medium">3</span>
                    <span>Click <strong>Export</strong> and download CSV</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-100 text-slate-700 text-xs flex items-center justify-center font-medium">4</span>
                    <span>Upload the file above</span>
                  </li>
                </ol>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="font-medium text-amber-800">Important notes</p>
                  <ul className="text-sm text-amber-700 mt-1 space-y-1">
                    <li>• YNAB sync pulls all transactions for the selected year</li>
                    <li>• Categories are automatically organized into Income, Bills, Needs, and Wants</li>
                    <li>• Existing spending data for the same month/category will be updated</li>
                    <li>• You can sync as often as you like - it won't create duplicates</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
